import { useMemo, useState } from "react";
import type { Subscription } from "./SubscriptionsTable";

// Plan monthly prices in euros
const PLAN_PRICES: Record<string, { monthly: number; annual: number }> = {
  basico:      { monthly: 49,  annual: 40  },
  profesional: { monthly: 99,  annual: 82  },
  premium:     { monthly: 199, annual: 165 },
};

function getPlanPrice(planId: string, billing: string | null): number {
  const prices = PLAN_PRICES[planId] ?? PLAN_PRICES.profesional;
  return billing === "annual" || billing === "yearly" ? prices.annual : prices.monthly;
}

function getMonthLabel(date: Date, short = false): string {
  return date.toLocaleDateString("es-ES", { month: short ? "short" : "long", year: "numeric" });
}

function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

interface MonthData {
  key: string;
  label: string;
  shortLabel: string;
  newMRR: number;
  newCount: number;
  churnedMRR: number;
  churnedCount: number;
  activeMRR: number;
  activeCount: number;
}

interface Props {
  subscriptions: Subscription[];
}

export default function MRRChart({ subscriptions }: Props) {
  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null);

  const { months, currentMRR, totalActive, mom, arr } = useMemo(() => {
    const now = new Date();
    const monthsData: MonthData[] = [];

    // Build 12-month window
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthsData.push({
        key: getMonthKey(d),
        label: getMonthLabel(d),
        shortLabel: d.toLocaleDateString("es-ES", { month: "short" }).replace(".", ""),
        newMRR: 0,
        newCount: 0,
        churnedMRR: 0,
        churnedCount: 0,
        activeMRR: 0,
        activeCount: 0,
      });
    }

    // Accumulate new MRR per month
    subscriptions.forEach((sub) => {
      const createdAt = new Date(sub.created_at);
      const key = getMonthKey(createdAt);
      const price = getPlanPrice(sub.plan_id, sub.billing);
      const month = monthsData.find((m) => m.key === key);
      if (month) {
        month.newMRR += price;
        month.newCount += 1;
      }

      // Churn: subscription is "canceled" and current_period_end falls in a month
      if (sub.status === "canceled" && sub.current_period_end) {
        const churnDate = new Date(sub.current_period_end);
        const churnKey = getMonthKey(churnDate);
        const churnMonth = monthsData.find((m) => m.key === churnKey);
        if (churnMonth) {
          churnMonth.churnedMRR += price;
          churnMonth.churnedCount += 1;
        }
      }
    });

    // Calculate cumulative active MRR
    let cumMRR = 0;
    let cumCount = 0;
    monthsData.forEach((month) => {
      cumMRR += month.newMRR - month.churnedMRR;
      cumCount += month.newCount - month.churnedCount;
      month.activeMRR = Math.max(0, cumMRR);
      month.activeCount = Math.max(0, cumCount);
    });

    const currentMonth = monthsData[monthsData.length - 1];
    const prevMonth = monthsData[monthsData.length - 2];
    const currentMRR = currentMonth?.activeMRR ?? 0;
    const prevMRR = prevMonth?.activeMRR ?? 0;
    const momPct = prevMRR > 0 ? ((currentMRR - prevMRR) / prevMRR) * 100 : 0;
    const totalActive = subscriptions.filter((s) => s.status === "active").length;

    return {
      months: monthsData,
      currentMRR,
      totalActive,
      mom: momPct,
      arr: currentMRR * 12,
    };
  }, [subscriptions]);

  const maxActiveMRR = Math.max(...months.map((m) => m.activeMRR), 1);
  const maxNewMRR = Math.max(...months.map((m) => m.newMRR), 1);

  // Chart dimensions
  const CHART_H = 180;
  const BAR_COUNT = months.length;

  const hovered = hoveredMonth ? months.find((m) => m.key === hoveredMonth) : null;

  // Plan breakdown
  const planBreakdown = useMemo(() => {
    const byPlan: Record<string, { count: number; mrr: number }> = {};
    subscriptions
      .filter((s) => s.status === "active")
      .forEach((sub) => {
        const key = sub.plan_id || "profesional";
        const price = getPlanPrice(sub.plan_id, sub.billing);
        if (!byPlan[key]) byPlan[key] = { count: 0, mrr: 0 };
        byPlan[key].count += 1;
        byPlan[key].mrr += price;
      });
    return Object.entries(byPlan).sort((a, b) => b[1].mrr - a[1].mrr);
  }, [subscriptions]);

  const planColors: Record<string, string> = {
    basico: "bg-stone-400",
    profesional: "bg-amber-500",
    premium: "bg-emerald-500",
  };

  const planLabels: Record<string, string> = {
    basico: "Básico",
    profesional: "Profesional",
    premium: "Premium",
  };

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-stone-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-emerald-50">
              <i className="ri-money-euro-circle-line text-emerald-600 text-base" />
            </div>
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">MRR actual</span>
          </div>
          <p className="text-3xl font-black text-stone-900">
            {currentMRR > 0 ? `€${currentMRR.toLocaleString("es-ES")}` : "€0"}
          </p>
          <p className="text-xs text-stone-400 mt-1">Ingresos recurrentes mensuales</p>
        </div>

        <div className="bg-white border border-stone-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${mom >= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
              <i className={`${mom >= 0 ? "ri-arrow-up-line text-emerald-600" : "ri-arrow-down-line text-rose-600"} text-base`} />
            </div>
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">Crecimiento MoM</span>
          </div>
          <p className={`text-3xl font-black ${mom >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {mom >= 0 ? "+" : ""}{mom.toFixed(1)}%
          </p>
          <p className="text-xs text-stone-400 mt-1">Vs. mes anterior</p>
        </div>

        <div className="bg-white border border-stone-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50">
              <i className="ri-trophy-line text-amber-600 text-base" />
            </div>
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">ARR proyectado</span>
          </div>
          <p className="text-3xl font-black text-stone-900">
            {arr > 0 ? `€${arr.toLocaleString("es-ES")}` : "€0"}
          </p>
          <p className="text-xs text-stone-400 mt-1">MRR × 12</p>
        </div>

        <div className="bg-white border border-stone-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100">
              <i className="ri-user-heart-line text-stone-600 text-base" />
            </div>
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">Suscriptores activos</span>
          </div>
          <p className="text-3xl font-black text-stone-900">{totalActive}</p>
          <p className="text-xs text-stone-400 mt-1">
            ARPU: {totalActive > 0 ? `€${Math.round(currentMRR / totalActive)}` : "—"}/mes
          </p>
        </div>
      </div>

      {/* Main chart + breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white border border-stone-100 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-sm font-bold text-stone-800">Evolución MRR — últimos 12 meses</h3>
              <p className="text-xs text-stone-400 mt-0.5">Barras = nuevo MRR · Línea = MRR acumulado</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" />
                <span className="text-xs text-stone-500">Nuevo MRR</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-5 h-0.5 bg-emerald-500 inline-block rounded-full" />
                <span className="text-xs text-stone-500">MRR acumulado</span>
              </div>
            </div>
          </div>

          {/* Tooltip */}
          {hovered && (
            <div className="mb-4 px-4 py-3 bg-stone-900 rounded-xl text-xs text-white flex items-center gap-6 flex-wrap">
              <span className="font-bold capitalize">{hovered.label}</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-amber-400 inline-block" />
                Nuevo: <strong>€{hovered.newMRR.toLocaleString("es-ES")}</strong> ({hovered.newCount} suscriptores)
              </span>
              {hovered.churnedMRR > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm bg-rose-400 inline-block" />
                  Churn: <strong>-€{hovered.churnedMRR.toLocaleString("es-ES")}</strong>
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-0.5 bg-emerald-400 inline-block" />
                Total: <strong>€{hovered.activeMRR.toLocaleString("es-ES")}</strong>
              </span>
            </div>
          )}

          {/* Chart container */}
          <div className="relative" style={{ height: `${CHART_H + 32}px` }}>
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between pr-2">
              {[1, 0.75, 0.5, 0.25, 0].map((pct) => (
                <span key={pct} className="text-xs text-stone-300 whitespace-nowrap">
                  €{Math.round(maxActiveMRR * pct).toLocaleString("es-ES")}
                </span>
              ))}
            </div>

            {/* Bars + line area */}
            <div className="absolute left-10 right-0 top-0 bottom-8">
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
                <div
                  key={pct}
                  className="absolute w-full border-t border-dashed border-stone-100"
                  style={{ bottom: `${pct * CHART_H}px` }}
                />
              ))}

              {/* Bars */}
              <div className="absolute bottom-0 left-0 right-0 flex items-end gap-1" style={{ height: `${CHART_H}px` }}>
                {months.map((m) => {
                  const barH = maxNewMRR > 0 ? (m.newMRR / maxNewMRR) * CHART_H * 0.85 : 0;
                  const churnH = maxNewMRR > 0 && m.churnedMRR > 0 ? (m.churnedMRR / maxNewMRR) * CHART_H * 0.85 : 0;
                  return (
                    <div
                      key={m.key}
                      className="flex-1 flex flex-col justify-end cursor-pointer group relative"
                      style={{ height: `${CHART_H}px` }}
                      onMouseEnter={() => setHoveredMonth(m.key)}
                      onMouseLeave={() => setHoveredMonth(null)}
                    >
                      {/* Churn bar (below) */}
                      {churnH > 0 && (
                        <div
                          className="w-full rounded-t-sm bg-rose-300 opacity-60 transition-all duration-200 group-hover:opacity-90"
                          style={{ height: `${churnH}px`, marginBottom: "1px" }}
                        />
                      )}
                      {/* New MRR bar */}
                      <div
                        className={`w-full rounded-t-sm transition-all duration-200 ${
                          hoveredMonth === m.key ? "bg-amber-400" : "bg-amber-300"
                        }`}
                        style={{ height: `${Math.max(barH, barH > 0 ? 2 : 0)}px` }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Trend line (MRR acumulado) */}
              <svg
                className="absolute left-0 right-0 top-0 bottom-0 w-full pointer-events-none"
                style={{ height: `${CHART_H}px` }}
                preserveAspectRatio="none"
                viewBox={`0 0 ${BAR_COUNT * 20} ${CHART_H}`}
              >
                <polyline
                  points={months.map((m, i) => {
                    const x = (i + 0.5) * 20;
                    const y = CHART_H - (maxActiveMRR > 0 ? (m.activeMRR / maxActiveMRR) * CHART_H * 0.95 : 0);
                    return `${x},${y}`;
                  }).join(" ")}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {months.map((m, i) => {
                  const x = (i + 0.5) * 20;
                  const y = CHART_H - (maxActiveMRR > 0 ? (m.activeMRR / maxActiveMRR) * CHART_H * 0.95 : 0);
                  return (
                    <circle
                      key={m.key}
                      cx={x}
                      cy={y}
                      r={hoveredMonth === m.key ? 3.5 : 2}
                      fill={hoveredMonth === m.key ? "#10b981" : "#6ee7b7"}
                      stroke="#fff"
                      strokeWidth="1"
                    />
                  );
                })}
              </svg>

              {/* X-axis labels */}
              <div className="absolute bottom-0 left-0 right-0 flex" style={{ transform: "translateY(100%)", paddingTop: "6px" }}>
                {months.map((m) => (
                  <div key={m.key} className="flex-1 text-center">
                    <span className={`text-xs capitalize transition-colors ${hoveredMonth === m.key ? "text-stone-800 font-bold" : "text-stone-400"}`}>
                      {m.shortLabel}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary row */}
          <div className="mt-8 pt-4 border-t border-stone-100 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-lg font-black text-amber-600">
                €{months.reduce((s, m) => s + m.newMRR, 0).toLocaleString("es-ES")}
              </p>
              <p className="text-xs text-stone-400">Nuevo MRR total (12m)</p>
            </div>
            <div>
              <p className="text-lg font-black text-rose-500">
                -€{months.reduce((s, m) => s + m.churnedMRR, 0).toLocaleString("es-ES")}
              </p>
              <p className="text-xs text-stone-400">Churn MRR total (12m)</p>
            </div>
            <div>
              <p className="text-lg font-black text-emerald-600">
                €{currentMRR.toLocaleString("es-ES")}
              </p>
              <p className="text-xs text-stone-400">MRR neto actual</p>
            </div>
          </div>
        </div>

        {/* Plan breakdown */}
        <div className="space-y-4">
          <div className="bg-white border border-stone-100 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-stone-800 mb-4">MRR por plan</h3>
            {planBreakdown.length === 0 ? (
              <div className="py-8 text-center text-xs text-stone-400">
                <i className="ri-bar-chart-line text-2xl text-stone-200 block mb-2" />
                Sin suscripciones activas
              </div>
            ) : (
              <div className="space-y-4">
                {planBreakdown.map(([planId, data]) => {
                  const pct = currentMRR > 0 ? Math.round((data.mrr / currentMRR) * 100) : 0;
                  return (
                    <div key={planId}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${planColors[planId] ?? "bg-stone-400"} inline-block`} />
                          <span className="text-xs font-semibold text-stone-700">
                            {planLabels[planId] ?? planId}
                          </span>
                          <span className="text-xs text-stone-400">({data.count})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-stone-800">€{data.mrr.toLocaleString("es-ES")}</span>
                          <span className="text-xs text-stone-400">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${planColors[planId] ?? "bg-stone-400"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {currentMRR > 0 && (
              <div className="mt-5 pt-4 border-t border-stone-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-stone-500">MRR total</span>
                  <span className="text-sm font-black text-stone-900">€{currentMRR.toLocaleString("es-ES")}</span>
                </div>
              </div>
            )}
          </div>

          {/* Monthly growth mini-table */}
          <div className="bg-white border border-stone-100 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-stone-800 mb-4">Últimos 3 meses</h3>
            <div className="space-y-3">
              {months.slice(-3).reverse().map((m) => {
                const diff = m.newMRR - m.churnedMRR;
                return (
                  <div key={m.key} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                    <span className="text-xs text-stone-500 capitalize font-medium">{m.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-amber-600 font-semibold">+€{m.newMRR}</span>
                      {m.churnedMRR > 0 && <span className="text-xs text-rose-500 font-semibold">-€{m.churnedMRR}</span>}
                      <span className={`text-xs font-bold ${diff >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {diff >= 0 ? "+" : ""}€{diff}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Price disclaimer */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-stone-400 text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-stone-400 leading-relaxed">
                Precios usados: Básico €49/mes · Profesional €99/mes · Premium €199/mes (anual ~15% dto.)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
